import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { ProductCard } from "@/components/site/ProductCard";
import { fetchCategory, fetchProductsByCategory } from "@/lib/catalog";
import { seo } from "@/lib/seo";

const catQO = (handle: string) =>
  queryOptions({
    queryKey: ["category", handle],
    queryFn: async () => {
      const cat = await fetchCategory(handle);
      if (!cat) return null;
      const products = await fetchProductsByCategory(cat.id);
      return { cat, products };
    },
  });

export const Route = createFileRoute("/collections/$handle")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(catQO(params.handle));
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: `${loaderData.cat.name} – Wholesale QT Foods`,
          description:
            loaderData.cat.description ??
            `Wholesale ${loaderData.cat.name.toLowerCase()} from QT Foods.`,
          image: loaderData.cat.image_url ?? undefined,
        })
      : [],
  }),
  component: Page,
  notFoundComponent: () => (
    <SiteLayout breadcrumbs={[{ label: "Catalog", href: "/collections" }, { label: "Not Found" }]}>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="font-serif text-4xl">Category not found</h1>
        <Link to="/collections" className="mt-6 inline-block text-primary hover:underline">
          ← Back to catalog
        </Link>
      </div>
    </SiteLayout>
  ),
});

function Page() {
  const { handle } = Route.useParams();
  const { data } = useSuspenseQuery(catQO(handle));
  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    if (!data?.products) return;
    const observer = new IntersectionObserver(
      (entries) => {
        let currentActives: string[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentActives.push(entry.target.id);
          }
        });
        if (currentActives.length > 0) {
          setActiveSection(currentActives[0]);
        }
      },
      { rootMargin: "-20% 0px -80% 0px" },
    );

    data.products.forEach((p) => {
      const el = document.getElementById(`product-${p.handle}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [data?.products]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (!data) return null;
  const { cat, products } = data;

  return (
    <SiteLayout breadcrumbs={[{ label: "Catalog", href: "/collections" }, { label: cat.name }]}>
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-5 pt-8 pb-16 lg:px-8 lg:pt-10 lg:pb-20">
          <h1 className="mt-4 font-serif text-5xl md:text-6xl">{cat.name}</h1>
          {cat.description && (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              {cat.description}
            </p>
          )}
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
        {products.length === 0 ? (
          <p className="text-muted-foreground">No products yet in this category.</p>
        ) : (
          <div className="flex flex-col lg:flex-row gap-12 items-start relative">
            <div className="flex-1 w-full order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-6 md:grid-cols-3 md:gap-8 lg:grid-cols-3">
                {products.map((p) => (
                  <div key={p.id} id={`product-${p.handle}`} className="scroll-mt-24">
                    <ProductCard product={p} categoryHandle={cat.handle} categoryName={cat.name} />
                  </div>
                ))}
              </div>
            </div>

            {products.length > 0 && (
              <aside className="hidden lg:block w-64 shrink-0 sticky top-24 order-1 lg:order-2 self-start border-l border-border pl-6">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 text-foreground/80">
                  On This Page
                </h3>
                <ul className="space-y-3 text-sm">
                  {products.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => scrollToSection(`product-${p.handle}`)}
                        className={`text-left transition-colors hover:text-primary ${
                          activeSection === `product-${p.handle}`
                            ? "text-primary font-medium"
                            : "text-muted-foreground"
                        }`}
                      >
                        {p.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>
            )}
          </div>
        )}
      </section>
    </SiteLayout>
  );
}
